import React from 'react';
import Modal from '../Modal/Modal';
import { Row, Col, Form, Input, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import Button from '../Button/Button';
import { checkIsFormValid } from '../../utils/formHelpers';
import { useForm } from 'antd/lib/form/Form';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../types/store';

type EmailVerificationModalProps = {
  visible: boolean;
  onClose: () => void;
};

type EmailVerificationFormValues = {
  newEmail: string;
};

const EmailVerificationModal = ({ visible }: EmailVerificationModalProps) => {
  const { t } = useTranslation();
  const [form] = useForm<EmailVerificationFormValues>();
  const [isFormValid, setIsFormValid] = useState(false);
  const [isResponseFetching, setIsResponseFetching] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);

  const {
    user: { updateUserEmail }
  } = useDispatch<AppDispatch>();

  const handleFieldsChange = () => {
    const isValid = checkIsFormValid(form, ['newEmail'], true);
    setIsFormValid(isValid);
  };

  const handleChangeEmail = () => {
    setIsEmailSent(false);
  };

  const handleFinish = (values: EmailVerificationFormValues) => {
    setIsResponseFetching(true);
    updateUserEmail(values.newEmail)
      .then(response => {
        setIsResponseFetching(false);
        const { isSuccess } = response;
        if (isSuccess) {
          setIsEmailSent(true);
        } else {
          // TODO: handling error logic
        }
      })
      .finally(() => setIsResponseFetching(false));
  };

  return (
    <Modal
      closable={false}
      visible={visible}
      footer={
        <Row style={{ justifyContent: 'end', width: '100%' }}>
          {isEmailSent ? (
            <Button size="middle" type="default" onClick={handleChangeEmail} data-testid="buttonChangeEmail">
              {t('modal.emailVerification.changeEmail')}
            </Button>
          ) : (
            <Button
              disabled={!isFormValid || isResponseFetching}
              size="middle"
              loading={isResponseFetching}
              onClick={() => form.submit()}
              data-testid="buttonVerifyEmail"
            >
              {t('modal.emailVerification.verifyButton')}
            </Button>
          )}
        </Row>
      }
    >
      <Row>
        <Col>
          <Typography.Title level={2}>
            {t(`modal.emailVerification.${isEmailSent ? 'titleEmailSent' : 'title'}`)}
          </Typography.Title>
        </Col>
      </Row>
      <Row>
        <Col xs={24} md={24} lg={24}>
          {isEmailSent ? (
            <>
              <Row style={{ justifyContent: 'center' }}>
                <Col>
                  <Typography.Text style={{ fontSize: 80 }}>📨 👈🏻</Typography.Text>
                </Col>
              </Row>
              <Row>
                <Typography.Text type="secondary">
                  {' '}
                  {t('modal.emailVerification.textEmailSent')}{' '}
                  <span style={{ fontWeight: 600 }}>{form.getFieldValue('newEmail')}</span>{' '}
                  {t('modal.emailVerification.toConfirmYourEmail')}
                </Typography.Text>
                <Typography.Text type="secondary">{t('modal.emailVerification.textPleaseClick')}</Typography.Text>
              </Row>
              <Row style={{ marginTop: '16px' }}>
                <Col>
                  <Typography.Text type="secondary"> {t('modal.emailVerification.cannotFind')}</Typography.Text>
                  <Button
                    onClick={() => form.submit()}
                    type="link"
                    disabled={!isFormValid || isResponseFetching}
                    loading={isResponseFetching}
                  >
                    <span style={{ textDecoration: 'underline' }}>{t('modal.emailVerification.resendEmail')}</span>
                  </Button>
                </Col>
              </Row>
            </>
          ) : (
            <Form
              form={form}
              id="emailVerificationForm"
              onValuesChange={handleFieldsChange}
              onFinish={handleFinish}
              requiredMark={false}
              data-testid="emailVerificationForm"
              layout="vertical"
              style={{ marginTop: '30px' }}
            >
              <Row>
                <Col xs={24}>
                  <Typography.Text type="secondary">{t('modal.emailVerification.text')}</Typography.Text>
                </Col>
              </Row>
              <Row style={{ marginTop: 16 }}>
                <Col xs={24}>
                  <Form.Item
                    name="newEmail"
                    label={t('modal.emailVerification.emailField.label')}
                    rules={[
                      { type: 'email', required: true, message: '' },
                      { required: true, message: '' }
                    ]}
                    data-testid="emailItem"
                  >
                    <Input data-testid="emailInput" size={'large'} placeholder="example@gmail.com" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          )}
        </Col>
      </Row>
    </Modal>
  );
};

export default EmailVerificationModal;
